import React from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import classnames from 'classnames';
import { trackLinkClick } from '@atlassian/temp-analytics';
import { BillTo, Quote, Maybe } from '@css-xp/graphtypes/src/generated-types-sxp';
import { NoData } from '@css-xp/ppc-common';
import { MILLISECONDS_IN_DAY } from '../../../../constants/widget';
import './widget-items.scss';

type Props = {
  url: string;
  company?: BillTo['companyName'];
  timestamp?: Maybe<string>;
  timestampPrefix?: React.ReactNode;
  timestampClassName?: string;
  testId?: string;
  belowDayTimestampContent?: React.ReactNode;
  heading?: string;
} & Pick<Quote, 'quoteNumber'>;
/**
 * Component for Quotes Widget Item on the Home page
 * @constructor
 */
const QuoteWidgetItemComponent = ({
  url,
  quoteNumber,
  timestamp,
  company,
  timestampPrefix = '',
  timestampClassName,
  belowDayTimestampContent,
  testId,
  heading = '',
}: Props) => {
  const renderTimestamp = () => {
    if (!timestamp) {
      return <NoData />;
    }

    return (
      <div className={classnames('timestamp', timestampClassName)} data-testid="quote-timestamp">
        {belowDayTimestampContent && new Date(timestamp).getTime() - new Date().getTime() <= MILLISECONDS_IN_DAY ? (
          belowDayTimestampContent
        ) : (
          <>
            {timestampPrefix} {formatDistanceToNowStrict(new Date(timestamp), { addSuffix: true })}
          </>
        )}
      </div>
    );
  };
  const quotesSubTitleClassName = company ? 'quotes-sub-title-present' : 'quotes-sub-title-absent';
  return (
    <div
      className={classnames(
        'flex align-center justify-space-between mb-12 pv-12 quote-widget-item',
        quotesSubTitleClassName
      )}
      data-testid={testId}
    >
      <div className="left-side">
        <div className="content">
          <Link
            to={url}
            onClick={() => {
              trackLinkClick('widgetItemClick', { source: heading });
            }}
          >
            <div className="title line-ellipsis">{quoteNumber}</div>
          </Link>
        </div>
        <div className="content">
          <div className="sub-title line-ellipsis">{company}</div>
        </div>
      </div>
      <div className="right-side">
        <div className="content">{renderTimestamp()}</div>
      </div>
    </div>
  );
};

export const QuoteWidgetItem = React.memo(QuoteWidgetItemComponent);
